import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'

export const useLoaderStore = defineStore('loaderstore', () => {
  const visible = ref(false)
  const counter = ref(0)

  const hidden = computed(() => !visible.value)

  const activeTasks = reactive(new Map())

  function startTask(taskId) {
    activeTasks.set(taskId, (activeTasks.get(taskId) ?? 0) + 1)
  }

  function endTask(taskId) {
    const count = activeTasks.get(taskId) ?? 0
    if (count <= 1) activeTasks.delete(taskId)
    else activeTasks.set(taskId, count - 1)
  }

  function show() {
    counter.value++
    if (counter.value >= 1) visible.value = true
  }

  function hide() {
    counter.value--
    if (counter.value <= 0) {
      counter.value = 0
      visible.value = false
    }
  }

  function reset() {
    counter.value = 0
    visible.value = false
    resetTasks()
  }

  function resetTasks() {
    activeTasks.clear()
  }

  function resetTask(taskId) {
    activeTasks.delete(taskId)
  }

  function isLoading(taskId) {
    return activeTasks.has(taskId)
  }

  /**
   * Uruchamia zadanie z loaderem.
   *
   * @param {Function} taskFn - Funkcja async do wykonania.
   * @param {Object} [options] - Opcje zadania.
   * @param {string} [options.taskId] - Opcjonalny identyfikator zadania do śledzenia stanu ładowania.
   * @param {Function} [options.onError] - Opcjonalna funkcja obsługi błędów.
   *
   * @returns {Promise<*>} - Zwraca wynik funkcji taskFn, jeśli zakończy się sukcesem.
   */
  async function run(taskFn, options = {}) {
    const { taskId = null, onError = null } = options

    if (typeof taskFn !== 'function') {
      console.warn('[loader.run] taskFn musi być funkcją')
      return
    }

    if (taskId) startTask(taskId)
    show()

    try {
      return await taskFn()
    } catch (err) {
      if (typeof onError === 'function') {
        onError(err)
      } else {
        console.error('[loader.run] Nieobsłużony błąd:', err)
      }
    } finally {
      if (taskId) endTask(taskId)
      hide()
    }
  }

  return {
    visible,
    hidden,
    counter,
    activeTasks,
    show,
    hide,
    reset,
    resetTasks,
    resetTask,
    isLoading,
    run
  }
})
